import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Grid, IconButton, Link, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { EmailVerification, EmailVerificationType, useAuth_VerifyEmailMutation } from 'queries';
import { Eye } from 'ts-react-feather-icons';
import * as yup from 'yup';

import FormTextField from 'common/form-text-field';
import Spinner from 'common/spinner';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

import PublicEstateDocumentUploadBox from './public-estate-document-upload-box';

const useStyles = makeStyles(() => ({
  root: {
    marginTop: '5vh',
    paddingLeft: '15vw',
    paddingRight: '15vw',
  },
  inputContainer: {
    marginTop: '2vh',
    marginBottom: '15px',
  },
  input: {
    display: 'flex',
    margin: 'auto',
  },
  inputError: {
    backgroundColor: '#FFF1F1',
    color: 'red',
  },
  actionContainer: {
    textAlign: 'center',
    marginTop: '50px',
    marginBottom: '50px',
  },
  renavigate: { marginTop: '25px', textDecoration: 'underline', cursor: 'pointer' },
  resetLink: {
    color: '#44D2F2',
    textDecoration: 'underline',
    cursor: 'pointer',
    marginBottom: '63px',
  },
  hidden: {
    display: 'none',
  },
  iconButton: {
    padding: '6px',
  },
}));

type Props = {
  emailVerification: Partial<EmailVerification>;
  verificationCode: string;
};

const schema = yup.object().shape({
  verificationCode: yup.string().required().min(6, 'Must be atleast 6 characters'),
  requirePassword: yup.boolean(),
  requireSocialSecurityNumber: yup.boolean(),
  password: yup.string().when('requirePassword', {
    is: true,
    then: (schema) =>
      schema.matches(
        /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&_-])[A-Za-z\d@$!%*#?&_-]{8,}$/,
        'Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and one special case Character',
      ),
  }),
  confirmPassword: yup
    .string()
    .nullable()
    .oneOf([yup.ref('password')], 'Passwords must match'),
  socialSecurityNumber: yup.string().when('requireSocialSecurityNumber', {
    is: true,
    then: (schema) =>
      schema.matches(/^\d{3}-?\d{2}-?\d{4}$/, 'Must be a valid social security number'),
  }),
});

type VerifyEmailFormInput = {
  verificationCode: string;
  password?: string;
  confirmPassword?: string;
  socialSecurityNumber?: string;
  requirePassword?: boolean;
  requireSocialSecurityNumber?: boolean;
};

const VerifyEmailForm = ({ emailVerification, verificationCode }: Props) => {
  const classes = useStyles();
  const history = useHistory();
  const [isVerified, setIsVerified] = useState(false);

  const [showPassword, setShowPassword] = useState(false); // user portal invitation
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [formDocumentIdFrontOfPhotoIdFile, setFormDocumentIdFrontOfPhotoIdFile] = useState<any>(); // client consent (truplanning and truproxy)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [formDocumentIdBackOfPhotoIdFile, setFormDocumentIdBackOfPhotoIdFile] = useState<any>(); // client consent (truplanning and truproxy)

  const [verifyEmail, { loading }] = useAuth_VerifyEmailMutation({
    onCompleted: () => setIsVerified(true),
    onError: (e) =>
      showAlert({
        message: 'Error verifying: ' + e.message,
        severity: NotificationSeverity.ERROR,
      }),
  });

  const isUserPortalNewUserInvitation =
    emailVerification.verificationType === EmailVerificationType.UserPortalInvitationNewUser;
  const isUserPortalExistingUserInvitation =
    emailVerification.verificationType === EmailVerificationType.UserPortalInvitationExistingUser;
  const isUserPortalNewClientInvitation =
    emailVerification.verificationType === EmailVerificationType.UserPortalInvitationNewClient;
  const isClientConsentTruplanning =
    emailVerification.verificationType === EmailVerificationType.EstateClientConsentTruplanning;
  const isClientConsentTruproxy =
    emailVerification.verificationType === EmailVerificationType.EstateClientConsentTruproxy;

  const requirePassword =
    isUserPortalNewUserInvitation ||
    isUserPortalExistingUserInvitation ||
    isUserPortalNewClientInvitation;
  const requireSocialSecurityNumber =
    (isClientConsentTruplanning && !emailVerification.hasClientSsn) ||
    (isClientConsentTruproxy && !emailVerification.hasWardSsn);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<VerifyEmailFormInput>({
    // @ts-expect-error - RHF V7 limitation #7895
    resolver: yupResolver(schema),
    values: {
      requirePassword,
      requireSocialSecurityNumber,
      verificationCode: verificationCode || '',
      password: '',
      confirmPassword: '',
      socialSecurityNumber: '',
    },
  });

  const onSubmit = async (formData: VerifyEmailFormInput) => {
    let password = null;
    let clientSsn = null;
    let wardSsn = null;
    let documentIdFrontOfPhotoIdFile = null;
    let documentIdBackOfPhotoIdFile = null;

    if (requirePassword) {
      password = formData.password;
    }

    if (isClientConsentTruplanning || isClientConsentTruproxy) {
      if (isClientConsentTruplanning && !emailVerification.hasClientSsn) {
        clientSsn = formData.socialSecurityNumber;
      }

      if (isClientConsentTruproxy && !emailVerification.hasClientSsn) {
        wardSsn = formData.socialSecurityNumber;
      }

      if (!emailVerification.hasDocumentIdFrontOfPhotoId) {
        documentIdFrontOfPhotoIdFile = formDocumentIdFrontOfPhotoIdFile;
      }

      if (!emailVerification.hasDocumentIdBackOfPhotoId) {
        documentIdBackOfPhotoIdFile = formDocumentIdBackOfPhotoIdFile;
      }
    }

    await verifyEmail({
      variables: {
        input: {
          id: emailVerification.id!,
          verificationCode: formData.verificationCode,
          password, // user portal invitation
          clientSsn, // truplanning
          wardSsn, // truproxy
          documentIdFrontOfPhotoIdFile, // truplanning and truproxy
          documentIdBackOfPhotoIdFile, // truplanning and truproxy
        },
      },
    });
  };

  useEffect(() => {
    if (verificationCode) {
      if (
        isClientConsentTruplanning &&
        emailVerification.hasClientSsn &&
        emailVerification.hasDocumentIdFrontOfPhotoId &&
        emailVerification.hasDocumentIdBackOfPhotoId
      ) {
        onSubmit({ verificationCode });
      } else if (
        isClientConsentTruproxy &&
        emailVerification.hasWardSsn &&
        emailVerification.hasDocumentIdFrontOfPhotoId &&
        emailVerification.hasDocumentIdBackOfPhotoId
      ) {
        onSubmit({ verificationCode });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <Spinner />;
  }

  const verifyButtonText = getVerifyButtonText();
  const verifiedTitle = getVerifiedTitle();
  const goBackLinkText = getGoBackLinkText();

  function getVerifyButtonText() {
    switch (emailVerification.verificationType) {
      case EmailVerificationType.UserRegistration:
        return 'Verify My Email';

      case EmailVerificationType.UserPortalInvitationNewUser:
      case EmailVerificationType.UserPortalInvitationExistingUser:
      case EmailVerificationType.UserPortalInvitationNewClient:
        return 'Submit Verification';

      case EmailVerificationType.EstateClientConsentTruadmin:
      case EmailVerificationType.EstateClientConsentTruplanning:
      case EmailVerificationType.EstateClientConsentTruproxy:
      case EmailVerificationType.EstateClientConsentTrustadmin:
        return 'I Consent';

      default:
        return 'Invalid verification type';
    }
  }

  function getVerifiedTitle() {
    switch (emailVerification.verificationType) {
      case EmailVerificationType.UserRegistration:
        return 'Your email was verified successfully.';

      case EmailVerificationType.UserPortalInvitationNewUser:
      case EmailVerificationType.UserPortalInvitationExistingUser:
      case EmailVerificationType.UserPortalInvitationNewClient:
        return 'Your verification was submitted successfully.';

      case EmailVerificationType.EstateClientConsentTruadmin:
      case EmailVerificationType.EstateClientConsentTruplanning:
      case EmailVerificationType.EstateClientConsentTruproxy:
      case EmailVerificationType.EstateClientConsentTrustadmin:
        return 'Client consent confirmed successfully.';

      default:
        return 'Invalid verification type';
    }
  }

  function getGoBackLinkText() {
    switch (emailVerification.verificationType) {
      case EmailVerificationType.UserRegistration:
        return 'Go back to the sign in page';

      case EmailVerificationType.UserPortalInvitationNewUser:
      case EmailVerificationType.UserPortalInvitationExistingUser:
      case EmailVerificationType.UserPortalInvitationNewClient:
      case EmailVerificationType.EstateClientConsentTruadmin:
      case EmailVerificationType.EstateClientConsentTruplanning:
      case EmailVerificationType.EstateClientConsentTruproxy:
      case EmailVerificationType.EstateClientConsentTrustadmin:
        return 'Go to the home page';

      default:
        return 'Invalid verification type';
    }
  }

  if (isVerified) {
    return (
      <div className={classes.actionContainer}>
        <Typography variant="h5">{verifiedTitle}</Typography>
        <Typography className={classes.renavigate}>
          <Link onClick={() => history.push('/')}>{goBackLinkText}</Link>
        </Typography>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={classes.root}>
      <div className={clsx(classes.inputContainer, verificationCode && classes.hidden)}>
        <Controller
          name="verificationCode"
          control={control}
          render={({ field: { ref, ...field } }) => (
            <FormTextField
              {...field}
              inputRef={ref}
              errorMessage={errors.verificationCode?.message}
              label="Verification Code"
            />
          )}
        />
      </div>
      {/* BEGIN USER PORTAL INVITATION */}
      {requirePassword && (
        <>
          <div className={classes.inputContainer}>
            <Controller
              name="password"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  type={showPassword ? 'text' : 'password'}
                  errorMessage={errors.password?.message}
                  label="Password"
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        className={classes.iconButton}
                        onClick={() => setShowPassword(!showPassword)}
                        size="large"
                      >
                        <Eye />
                      </IconButton>
                    ),
                  }}
                />
              )}
            />
          </div>
          <div className={classes.inputContainer}>
            <Controller
              name="confirmPassword"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  type="password"
                  ref={ref}
                  errorMessage={errors.confirmPassword?.message}
                  label="Confirm Password"
                />
              )}
            />
          </div>
        </>
      )}
      {/* END USER PORTAL INVITATION */}
      {/* BEGIN CLIENT CONSENT TRUPLANNING and TRUPROXY */}
      {((isClientConsentTruplanning &&
        (!emailVerification.hasClientSsn ||
          !emailVerification.hasDocumentIdFrontOfPhotoId ||
          !emailVerification.hasDocumentIdBackOfPhotoId)) ||
        (isClientConsentTruproxy &&
          (!emailVerification.hasWardSsn ||
            !emailVerification.hasDocumentIdFrontOfPhotoId ||
            !emailVerification.hasDocumentIdBackOfPhotoId))) && (
        <>
          <br />
          <Typography>
            At Trustate, your information and the security of that information is of the utmost
            importance. In order to protect that information, we must first verify your identity.
            Please submit the following information.
          </Typography>
        </>
      )}
      {requireSocialSecurityNumber && (
        <>
          <br />
          {isClientConsentTruproxy && (
            <>
              <br />
              <Typography>
                Please enter the SSN of the person for whom you are acting as Attorney-in-Fact or
                Guardian here:
              </Typography>
            </>
          )}
          <div className={classes.inputContainer}>
            <Controller
              name="socialSecurityNumber"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  ref={ref}
                  inputProps={{ maxLength: 80 }}
                  errorMessage={errors.socialSecurityNumber?.message}
                  label="Social Security Number"
                />
              )}
            />
          </div>
        </>
      )}
      {(isClientConsentTruplanning || isClientConsentTruproxy) &&
        (!emailVerification.hasDocumentIdFrontOfPhotoId ||
          !emailVerification.hasDocumentIdBackOfPhotoId) && (
          <>
            <br />
            {isClientConsentTruproxy && (
              <>
                <br />
                <Typography>
                  Please upload <strong>YOUR</strong> photo ID here:
                </Typography>
              </>
            )}
            <Grid container>
              <PublicEstateDocumentUploadBox
                estateDocumentField="documentIdFrontOfPhotoId"
                uploadedFile={formDocumentIdFrontOfPhotoIdFile}
                setUploadedFile={setFormDocumentIdFrontOfPhotoIdFile}
              />
              <PublicEstateDocumentUploadBox
                estateDocumentField="documentIdBackOfPhotoId"
                uploadedFile={formDocumentIdBackOfPhotoIdFile}
                setUploadedFile={setFormDocumentIdBackOfPhotoIdFile}
              />
            </Grid>
          </>
        )}
      {/* END CLIENT CONSENT TRUPLANNING and TRUPROXY */}
      <div className={classes.actionContainer}>
        <Button type="submit" variant="contained" color="secondary">
          {verifyButtonText}
        </Button>
        <Typography className={classes.renavigate}>
          <Link onClick={() => history.push('/')}>{goBackLinkText}</Link>
        </Typography>
      </div>
    </form>
  );
};

export default VerifyEmailForm;
